import {
  Bot,
  ClipboardCheck,
  Handshake,
  Crosshair,
  Facebook,
  FileCheck2,
  FolderKanban,
  IdCard,
  KeyRound,
  LayoutDashboard,
  LibraryBig,
  Lightbulb,
  ListChecks,
  PanelsTopLeft,
  PhoneCall,
  PhoneIncoming,
  Radar,
  Star,
  Sunrise,
  Tags,
  Target,
  Users,
  type LucideIcon,
  BarChart3,
  Binoculars,
  Flame,
  Images,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import { pageMatches, routeMode } from "./sidebar-mode";
import { tierLeavesOut } from "./tiers";
import { servicesLeaveOut } from "./services";

/** `idInPath`: the page's path carries the client id (`<to>/<id>`) instead of `?client_id=`. */
/** `keywords`: other words people search for this page with (Search matches them too). */
export type NavTab = { to: string; label: string; idInPath?: boolean; end?: boolean; view?: string; keywords?: string };
/**
 * `tabs`: related pages that share this one sidebar entry. The entry links to the
 * first tab, stays highlighted on every tab, and Layout shows the tabs above the
 * page (SectionTabs). The pages keep their own URLs. `view` is a `?view=` value.
 */
export type NavItem = NavTab & { icon: LucideIcon; tabs?: NavTab[] };
export type NavGroup = { label: string; items: NavItem[] };

/**
 * The agency's own work: what each person does today, plus the whole portfolio.
 * None of these pages read the selected client.
 */
export const AGENCY_GROUPS: NavGroup[] = [
  {
    label: "My day",
    items: [
      {
        to: "/", label: "Agency overview", icon: LayoutDashboard, end: true,
        tabs: [
          { to: "/", label: "Agency overview", end: true },
          { to: "/", label: "Analytics", end: true, view: "analytics" },
        ],
      },
      // The agency-wide assistant (Neil, 2026-10-31); its box sits on Agency overview.
      { to: "/jarvis", label: "Jarvis", icon: Bot, keywords: "ai assistant chat ask agent claude openai" },
      // Its own entry (Shawn, 2026-10-09, could not find it as a tab under Agency overview).
      { to: "/standup", label: "Morning standup", icon: Sunrise, keywords: "standup stand-up daily briefing my week deadlines due" },
      { to: "/tasks", label: "Work queue", icon: ClipboardCheck },
      { to: "/approvals", label: "Approvals", icon: FileCheck2 },
    ],
  },
  {
    label: "Agency",
    items: [
      {
        to: "/ads", label: "Portfolio", icon: BarChart3,
        tabs: [
          { to: "/ads", label: "Ads" },
          { to: "/health", label: "Client health" },
          { to: "/websites", label: "Website visitors", keywords: "websites sites traffic" },
          { to: "/conversions", label: "Conversions" },
        ],
      },
      {
        // The agency's own sales (Shawn, 2026-10-05): leads from forms and booking pages.
        to: "/crm", label: "CRM", icon: Handshake,
        tabs: [
          { to: "/crm", label: "Pipeline", end: true, keywords: "sales deals board" },
          { to: "/crm/contacts", label: "Contacts", keywords: "leads people" },
          { to: "/crm/forms", label: "Forms", keywords: "lead form survey funnel embed" },
          { to: "/crm/bookings", label: "Bookings", keywords: "calendar booking calls meetings" },
          { to: "/crm/drips", label: "Drips", keywords: "email sequence nurture campaign automation drip" },
        ],
      },
      {
        // Landing pages built like Orbit's, on the agency's own domains (Neil, 2026-10-12).
        // The editor (/pages/<id>) fills the screen, outside the sidebar layout (App.tsx).
        to: "/pages", label: "Pages", icon: PanelsTopLeft,
        keywords: "landing page funnel website builder publish domain template",
        tabs: [
          { to: "/pages", label: "Pages", end: true, keywords: "landing page funnel website builder publish template" },
          { to: "/pages/domains", label: "Domains", keywords: "custom domain dns website address homepage" },
        ],
      },
      { to: "/traction", label: "Traction", icon: Target },
      { to: "/agency/projects", label: "Projects", icon: FolderKanban },
      { to: "/team", label: "Team", icon: Users },
      {
        to: "/knowledge", label: "Library", icon: LibraryBig,
        tabs: [
          { to: "/knowledge", label: "Knowledge base" },
          { to: "/prompts", label: "Prompt library" },
          { to: "/training", label: "Training" },
        ],
      },
    ],
  },
];

/**
 * One client's pages, grouped by service line so each kind of work has one
 * home (Shawn, 2026-10-01: "all calls in the same heading, GBP and SEO in
 * their own"). Every one of these reads the selected client, so the sidebar
 * links them with `?client_id=` (or puts the id in the path, `idInPath`) and
 * the page cannot disagree with the name at the top of the sidebar.
 */
export const CLIENT_GROUPS: NavGroup[] = [
  {
    label: "",
    items: [
      { to: "/dashboard", label: "Client dashboard", icon: LayoutDashboard },
      { to: "/clients", label: "Profile", icon: IdCard, idInPath: true },
      { to: "/analyst", label: "AI analyst", icon: Bot },
      { to: "/compare", label: "Compare to peers", icon: Users, keywords: "peers benchmark franchise same brand performance director" },
    ],
  },
  {
    label: "Calls",
    items: [
      { to: "/call-intelligence", label: "Calls & coaching", icon: PhoneCall },
      { to: "/call-center", label: "Call center reports", icon: PhoneIncoming },
    ],
  },
  {
    label: "Google Ads",
    items: [
      {
        to: "/ads", label: "Ads report", icon: BarChart3, idInPath: true,
        tabs: [
          { to: "/ads", label: "Ads report", idInPath: true },
          { to: "/ads-campaigns", label: "Campaigns", keywords: "run ads new campaign pause budget google ads" },
          { to: "/ads-ai", label: "Ads AI", keywords: "ai agent assistant optimize google ads chat" },
          { to: "/ads-review", label: "Weekly review" },
          { to: "/ad-spend", label: "Ad spend" },
        ],
      },
      { to: "/tracking", label: "Tracking", icon: Crosshair },
    ],
  },
  {
    // Neil, 2026-10-09: "make everything easy to find, proper labels". Competitors was a
    // third tab under Facebook although it reads Instagram too; it has its own entry now.
    label: "Social",
    items: [
      {
        // Ads and the page read Meta through the client's Windsor links.
        to: "/facebook-ads", label: "Facebook", icon: Facebook,
        tabs: [
          { to: "/facebook-ads", label: "Ads", keywords: "facebook instagram meta ads campaigns pause budget" },
          { to: "/facebook-page", label: "Page", keywords: "facebook organic page posts followers publish post write a post social media" },
        ],
      },
      { to: "/competitors", label: "Competitors", icon: Binoculars, keywords: "competitor ads ad library spy compare rivals instagram posts find competitors outliers" },
      { to: "/publishing", label: "Publishing", icon: CalendarClock, keywords: "facebook google business profile gbp post schedule calendar social publishing" },
    ],
  },
  {
    label: "Content",
    items: [
      { to: "/content-ideas", label: "Content pipeline", icon: Lightbulb, keywords: "content ideas post ideas generate ai hooks board kanban write it social media" },
      { to: "/viral", label: "Viral posts", icon: Flame, keywords: "viral trending tiktok instagram reels outliers what is working hooks top posts" },
      { to: "/content", label: "Content review", icon: FileCheck2, keywords: "drafts approve approval posted content" },
      { to: "/creative", label: "Creative hub", icon: Images, keywords: "make an image generate image picture graphic brand files google drive canva copy check grammar spelling posting queue publish" },
    ],
  },
  {
    label: "GBP & SEO",
    items: [
      { to: "/seo-plan", label: "SEO plan", icon: ListChecks, keywords: "seo agent weekly plan google business posts faq questions review replies schema llms" },
      { to: "/reputation", label: "Reviews & citations", icon: Star },
      { to: "/keywords", label: "Keywords", icon: Tags },
      { to: "/ai-search", label: "AI search", icon: Sparkles, keywords: "aeo ai answers chatgpt gemini claude geo llm visibility" },
      { to: "/website", label: "Website analytics", icon: Radar },
    ],
  },
  {
    label: "Projects & setup",
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban },
      { to: "/sops", label: "SOPs", icon: ListChecks },
      {
        // Client settings' own sections are Setup tabs (one row of tabs, not two).
        to: "/onboarding", label: "Setup", icon: KeyRound,
        tabs: [
          { to: "/onboarding", label: "Onboarding" },
          { to: "/onboarding", label: "Checklist", view: "checklist", keywords: "setup checklist access waiting on client" },
          { to: "/client-settings", label: "Connections", keywords: "client settings integrations accounts" },
          { to: "/client-settings", label: "Portal", view: "tools" },
          { to: "/client-settings", label: "Slack", view: "slack" },
          { to: "/client-settings", label: "API keys", view: "keys" },
        ],
      },
    ],
  },
];

/** Every page an entry stands for: its tabs, or the entry itself. */
export const entryPages = (item: NavItem): NavTab[] => item.tabs ?? [item];
const pagesOf = (groups: NavGroup[]) =>
  groups.flatMap((g) => g.items.flatMap((item) => entryPages(item).map((page) => ({ page, item, group: g }))));
/** Every page in the sidebar once, with the entry and group it sits under (search, breadcrumbs). */
export const AGENCY_PAGES = pagesOf(AGENCY_GROUPS).filter((p) => !p.page.view);
export const CLIENT_PAGES = pagesOf(CLIENT_GROUPS).filter((p) => !p.page.view);

export const AGENCY_PATHS = [...new Set(AGENCY_PAGES.map((p) => p.page.to))];
/** Pages linked with `?client_id=`. The `idInPath` ones are matched by `clientIdPage`, and
 * "/ads" must stay out of this list or the agency's Ads page would open the Clients half. */
export const CLIENT_PATHS = CLIENT_PAGES.filter((p) => !p.page.idInPath).map((p) => p.page.to);

/** Where a client page lives for one client, with its `?view=` tab when it has one. */
export const clientLink = (item: NavTab, clientId: number) =>
  item.idInPath
    ? `${item.to}/${clientId}${item.view ? `?view=${item.view}` : ""}`
    : `${item.to}?client_id=${clientId}${item.view ? `&view=${item.view}` : ""}`;

/**
 * The client pages this person may open for this client: Client settings is
 * superadmin-only, a tier can leave delivery pages out (tiers.ts), and a service
 * switched off for the client leaves its pages out (services.ts).
 */
export const canOpenClientPage = (page: NavTab, role: string | undefined, tier: string | null | undefined, servicesOff?: readonly string[] | null) =>
  (page.to !== "/client-settings" || role === "superadmin") && !tierLeavesOut(tier, page.to) && !servicesLeaveOut(servicesOff, page.to);

/** The sidebar entry (and the page within it) for the URL on screen, in whichever half owns the route. */
export function currentEntry(pathname: string, search: string) {
  const clientSide = routeMode(pathname, CLIENT_PATHS, AGENCY_PATHS) === "clients";
  for (const group of clientSide ? CLIENT_GROUPS : AGENCY_GROUPS)
    for (const item of group.items) {
      const page = entryPages(item).find((p) => pageMatches(pathname, search, p, item.tabs));
      if (page) return { item, page, group, clientSide };
    }
  return null;
}
